/**
 * Collings Property Validation Utility
 * 
 * This module contains validation rules for Collings properties based on the backend REA API.
 * Properties must meet these criteria to be successfully uploaded to Collings/REA.
 */

/**
 * Collings Property Requirements (from backend API):
 * - Price: Minimum $2,900
 * - Bedrooms/Bathrooms/Garages: Maximum 50 each, minimum 0
 * - Photos: Minimum 1, Maximum 5 images
 * - Required Fields: property_type, street, suburb, state, postcode, category, headline, description
 * - Property Type: Must be one of the valid choices
 * - Field Lengths: headline max 255 chars, street max 100 chars, etc.
 */

export const COLLINGS_REQUIREMENTS = {
    MIN_PRICE: 2900,
    MAX_BEDROOMS: 50,
    MAX_BATHROOMS: 50,
    MAX_GARAGES: 50,
    MIN_PHOTOS: 1,
    MAX_PHOTOS: 35, // REA's per-listing maximum; matches the backend cap
    MAX_HEADLINE_LENGTH: 255,
    MAX_STREET_LENGTH: 100,
    MAX_SUBURB_LENGTH: 100,
    MAX_STATE_LENGTH: 30,
    MAX_POSTCODE_LENGTH: 30,
    MAX_CATEGORY_LENGTH: 50,
    MAX_SUB_NUMBER_LENGTH: 50,
    MAX_STREET_NUMBER_LENGTH: 50,
    MAX_PROPERTY_CLASS_LENGTH: 100,
    VALID_PROPERTY_TYPES: [
        'residential',
        'commercial',
        'business',
        'land',
        'rural',
        'rental',
        'holidayRental'
    ],
    REQUIRED_FIELDS: [
        'property_type',
        'street',
        'suburb',
        'state',
        'postcode',
        'category',
        'headline',
        'description'
    ]
};

/**
 * Validates if a property meets Collings/REA API requirements
 * @param {Object} propertyData - The property data to validate
 * @returns {Object} - { isValid: boolean, errors: string[], warnings: string[] }
 */
export const validateCollingsProperty = (propertyData) => {
    const errors = [];
    const warnings = [];

    // 1. Price Validation
    const price = parsePrice(propertyData.price);
    if (price && price < COLLINGS_REQUIREMENTS.MIN_PRICE) {
        errors.push(
            `Price must be at least $${COLLINGS_REQUIREMENTS.MIN_PRICE.toLocaleString()}. Current price: $${price.toLocaleString()}`
        );
    }

    // 2. Bedroom Validation
    const bedrooms = parseInt(propertyData.beds || propertyData.bedrooms || 0);
    if (bedrooms > COLLINGS_REQUIREMENTS.MAX_BEDROOMS) {
        errors.push(
            `Maximum ${COLLINGS_REQUIREMENTS.MAX_BEDROOMS} bedrooms allowed. Current bedrooms: ${bedrooms}`
        );
    }
    if (bedrooms < 0) {
        errors.push('Bedrooms cannot be negative');
    }

    // 3. Bathroom Validation
    const bathrooms = parseInt(propertyData.baths || propertyData.bathrooms || 0);
    if (bathrooms > COLLINGS_REQUIREMENTS.MAX_BATHROOMS) {
        errors.push(
            `Maximum ${COLLINGS_REQUIREMENTS.MAX_BATHROOMS} bathrooms allowed. Current bathrooms: ${bathrooms}`
        );
    }
    if (bathrooms < 0) {
        errors.push('Bathrooms cannot be negative');
    }

    // 4. Garage/Parking Validation
    const garages = parseInt(propertyData.parking || propertyData.garages || 0);
    if (garages > COLLINGS_REQUIREMENTS.MAX_GARAGES) {
        errors.push(
            `Maximum ${COLLINGS_REQUIREMENTS.MAX_GARAGES} parking spaces allowed. Current parking: ${garages}`
        );
    }
    if (garages < 0) {
        errors.push('Parking spaces cannot be negative');
    }

    // 5. Photos Validation
    const photoCount = getPhotoCount(propertyData.images);
    if (photoCount < COLLINGS_REQUIREMENTS.MIN_PHOTOS) {
        errors.push(
            `Minimum ${COLLINGS_REQUIREMENTS.MIN_PHOTOS} photo required. Current photos: ${photoCount}`
        );
    }
    if (photoCount > COLLINGS_REQUIREMENTS.MAX_PHOTOS) {
        errors.push(
            `Maximum ${COLLINGS_REQUIREMENTS.MAX_PHOTOS} photos allowed. Current photos: ${photoCount}. Please remove ${photoCount - COLLINGS_REQUIREMENTS.MAX_PHOTOS} image(s).`
        );
    }

    // 6. Agent Validation (Required)
    const agentId = propertyData.agentId || propertyData.agent_id;
    if (!agentId || (typeof agentId === 'string' && !agentId.trim())) {
        errors.push('Agent is required. Please select a primary agent.');
    }

    // 7. Commission Validation (Required)
    const commissionType = propertyData.commission_type || 'percentage';
    if (!commissionType) {
        errors.push('Commission type is required. Choose either "percentage" or "fixed".');
    } else if (commissionType === 'percentage') {
        if (propertyData.commission_rate === null || propertyData.commission_rate === undefined || propertyData.commission_rate === '') {
            errors.push('Commission rate is required when commission type is "percentage".');
        } else {
            const rate = parseFloat(propertyData.commission_rate);
            if (isNaN(rate) || rate < 0) {
                errors.push('Commission rate must be a positive number.');
            } else if (rate > 100) {
                errors.push('Commission rate cannot exceed 100%.');
            }
        }
    } else if (commissionType === 'fixed') {
        if (!propertyData.commission_amount || propertyData.commission_amount === '') {
            errors.push('Commission amount is required when commission type is "fixed".');
        } else {
            const amount = parsePrice(propertyData.commission_amount);
            if (isNaN(amount) || amount <= 0) {
                errors.push('Commission amount must be a positive number.');
            }
        }
    }

    // 8. Required Fields Validation
    const requiredFieldsMap = {
        property_type: propertyData.propertyTypes || propertyData.property_type,
        street: propertyData.address || propertyData.street,
        suburb: propertyData.suburb,
        state: propertyData.state,
        postcode: propertyData.postal_code || propertyData.postcode,
        category: propertyData.type || propertyData.category || propertyData.propertyTypes,
        headline: propertyData.title || propertyData.headline,
        description: propertyData.description
    };

    COLLINGS_REQUIREMENTS.REQUIRED_FIELDS.forEach(field => {
        const value = requiredFieldsMap[field];
        if (!value || (typeof value === 'string' && !value.trim())) {
            const fieldName = field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
            errors.push(`${fieldName} is required`);
        }
    });

    // 9. Property Type Validation
    // Note: Form uses values like "Apartment", "House", "Unit", etc.
    // These get mapped to REA API values (residential, commercial, etc.) during submission
    // So we validate against the form values, not the REA API values
    const VALID_FORM_PROPERTY_TYPES = [
        'Apartment',
        'Unit',
        'House',
        'Townhouse',
        'Villa',
        'House and Land',
        'Land',
        'Block of Units',
        'Commercial',
        'Off the Plan Apartments',
        'residential',
        'commercial',
        'business',
        'land',
        'rural',
        'rental',
        'holidayRental'
    ];
    
    const propertyType = propertyData.propertyTypes || propertyData.property_type || '';
    if (propertyType && !VALID_FORM_PROPERTY_TYPES.includes(propertyType)) {
        errors.push(
            `Invalid property type "${propertyType}". Please select a valid property type from the form.`
        );
    }

    // 9. Field Length Validations
    const headline = propertyData.title || propertyData.headline || '';
    if (headline.length > COLLINGS_REQUIREMENTS.MAX_HEADLINE_LENGTH) {
        errors.push(
            `Headline must be ${COLLINGS_REQUIREMENTS.MAX_HEADLINE_LENGTH} characters or less. Current: ${headline.length}`
        );
    }

    const street = propertyData.address || propertyData.street || '';
    if (street.length > COLLINGS_REQUIREMENTS.MAX_STREET_LENGTH) {
        errors.push(
            `Street must be ${COLLINGS_REQUIREMENTS.MAX_STREET_LENGTH} characters or less. Current: ${street.length}`
        );
    }

    const suburb = propertyData.suburb || '';
    if (suburb.length > COLLINGS_REQUIREMENTS.MAX_SUBURB_LENGTH) {
        errors.push(
            `Suburb must be ${COLLINGS_REQUIREMENTS.MAX_SUBURB_LENGTH} characters or less. Current: ${suburb.length}`
        );
    }

    const state = propertyData.state || '';
    if (state.length > COLLINGS_REQUIREMENTS.MAX_STATE_LENGTH) {
        errors.push(
            `State must be ${COLLINGS_REQUIREMENTS.MAX_STATE_LENGTH} characters or less. Current: ${state.length}`
        );
    }

    const postcode = propertyData.postal_code || propertyData.postcode || '';
    if (postcode.length > COLLINGS_REQUIREMENTS.MAX_POSTCODE_LENGTH) {
        errors.push(
            `Postcode must be ${COLLINGS_REQUIREMENTS.MAX_POSTCODE_LENGTH} characters or less. Current: ${postcode.length}`
        );
    }

    return {
        isValid: errors.length === 0,
        errors,
        warnings,
    };
};

/**
 * Parse price from various formats
 * @param {string|number} price - Price in various formats ($1,000,000 or 1000000)
 * @returns {number} - Numeric price value
 */
const parsePrice = (price) => {
    if (typeof price === 'number') return price;
    if (typeof price === 'string') {
        // Remove $, commas, and any other non-numeric characters except decimal point
        const numericPrice = price.replace(/[$,]/g, '');
        return parseFloat(numericPrice) || 0;
    }
    return 0;
};

/**
 * Get count of photos from images array
 * @param {Array} images - Array of images (can be File objects, URLs, or objects with file property)
 * @returns {number} - Count of valid images
 */
const getPhotoCount = (images) => {
    if (!Array.isArray(images)) return 0;

    // Filter out any invalid entries
    return images.filter(img => {
        if (!img) return false;

        // Handle File objects
        if (img instanceof File) {
            return img.type.startsWith('image/');
        }

        // Handle URL strings
        if (typeof img === 'string') {
            return img.length > 0;
        }

        // Handle objects with file property
        if (img.file) {
            if (img.file instanceof File) {
                return img.file.type.startsWith('image/');
            }
            if (typeof img.file === 'string') {
                return img.file.length > 0;
            }
        }

        return false;
    }).length;
};

/**
 * Show validation errors to user
 * @param {Array} errors - Array of error messages
 * @param {Function} toastFn - Toast notification function
 */
export const showValidationErrors = (errors, toastFn) => {
    if (errors.length === 0) return;

    // Show first error as main toast
    toastFn.error(errors[0], { duration: 5000 });

    // If multiple errors, show them in console for debugging
    if (errors.length > 1) {
        console.error('Collings Property Validation Errors:', errors);
    }
};

/**
 * Get a summary of Collings requirements for display
 * @returns {Object} - Requirements summary
 */
export const getRequirementsSummary = () => {
    return {
        price: `Minimum $${COLLINGS_REQUIREMENTS.MIN_PRICE.toLocaleString()}`,
        bedrooms: `Maximum ${COLLINGS_REQUIREMENTS.MAX_BEDROOMS}`,
        bathrooms: `Maximum ${COLLINGS_REQUIREMENTS.MAX_BATHROOMS}`,
        parking: `Maximum ${COLLINGS_REQUIREMENTS.MAX_GARAGES}`,
        photos: `${COLLINGS_REQUIREMENTS.MIN_PHOTOS}-${COLLINGS_REQUIREMENTS.MAX_PHOTOS} photos`,
        propertyTypes: COLLINGS_REQUIREMENTS.VALID_PROPERTY_TYPES.join(', '),
        requiredFields: COLLINGS_REQUIREMENTS.REQUIRED_FIELDS.map(f =>
            f.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
        ).join(', ')
    };
};

/**
 * Check if property data meets minimum requirements for validation
 * @param {Object} propertyData 
 * @returns {boolean}
 */
export const hasMinimumDataForValidation = (propertyData) => {
    return !!(propertyData && (propertyData.price || propertyData.beds || propertyData.images));
};
